import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Input } from '../../design-system/components/Input';
import { spacing } from '../../design-system/tokens/spacing';
import { animations } from '../../design-system/tokens/animations';
import LoadingSpinner from './LoadingSpinner';

const SearchBarContainer = styled.div<{ fullWidth: boolean }>`
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
  position: relative;
`;

const ClearButton = styled.button`
  position: absolute;
  right: ${spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  padding: ${spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.colors.textSecondary};
  border-radius: ${props => props.theme.borders?.radius?.sm || '4px'};
  transition: ${animations.transition(
    ['color', 'background-color'],
    animations.duration.fast,
    animations.easing.easeOut
  )};
  z-index: 10;

  &:hover {
    color: ${props => props.theme.colors.text};
    background: ${props => props.theme.colors.surfaceHover};
  }

  &:focus {
    outline: 2px solid ${props => props.theme.colors.primary};
    outline-offset: 2px;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingWrapper = styled.div`
  position: absolute;
  right: ${spacing[3]};
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
`;

export interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  onSearch?: (value: string) => void; // Called on debounced change
  placeholder?: string;
  debounceMs?: number; // Default 300
  loading?: boolean;
  disabled?: boolean;
  fullWidth?: boolean;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  onSearch,
  placeholder,
  debounceMs = 300,
  loading = false,
  disabled = false,
  fullWidth = false,
  className,
  id,
  'aria-label': ariaLabel,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t('common.searchPlaceholder', { defaultValue: 'Search...' });
  const resolvedAriaLabel = ariaLabel ?? t('common.search');
  const [internalValue, setInternalValue] = useState(value);

  // Sync internal value with external value prop
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Debounce effect for onSearch callback
  useEffect(() => {
    if (!onSearch) return;

    const timer = setTimeout(() => {
      onSearch(internalValue);
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [internalValue, debounceMs, onSearch]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setInternalValue(newValue);
      onChange(newValue);
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setInternalValue('');
    onChange('');
    if (onSearch) {
      onSearch('');
    }
  }, [onChange, onSearch]);

  const showClearButton = internalValue.length > 0 && !loading && !disabled;

  return (
    <SearchBarContainer fullWidth={fullWidth} className={className}>
      <Input
        id={id}
        type="text"
        value={internalValue}
        onChange={handleChange}
        placeholder={resolvedPlaceholder}
        variant="outlined"
        iconLeft={<MagnifyingGlassIcon style={{ width: '20px', height: '20px' }} />}
        iconRight={
          loading ? (
            <LoadingWrapper>
              <LoadingSpinner size="sm" />
            </LoadingWrapper>
          ) : showClearButton ? (
            <ClearButton
              type="button"
              onClick={handleClear}
              aria-label={t('common.clearSearch', { defaultValue: 'Clear search' })}
              tabIndex={0}
            >
              <XMarkIcon />
            </ClearButton>
          ) : undefined
        }
        disabled={disabled}
        fullWidth={fullWidth}
        aria-label={resolvedAriaLabel}
      />
    </SearchBarContainer>
  );
};

export default SearchBar;
